'use client'
import { useLocalStore } from "@/utils/localStore"
import { Pitch, Pitcher } from "@/utils/types"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@repo/web-ui/components/base/alert-dialog"
import { Button } from "@repo/web-ui/components/base/button"
import { ClickableCard } from "@repo/web-ui/components/base/clickable-card"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@repo/web-ui/components/base/collapsible"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@repo/web-ui/components/base/dropdown-menu"
import { Input } from "@repo/web-ui/components/base/input"
import { Label } from "@repo/web-ui/components/base/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@repo/web-ui/components/base/select"
import { Check, Copy, Edit, Minus, MoreHorizontal, Pencil, PersonStanding, Plus, Trash2 } from 'lucide-react'
import { useRouter } from "next/navigation"
import { Dispatch, RefObject, SetStateAction, useEffect, useRef, useState } from "react"
import * as THREE from 'three'
import { v4 as uuidv4 } from 'uuid'

export default function Page() {
    const router = useRouter()
    const { values, setValues } = useLocalStore()
    const [openPitchers, setOpenPitchers] = useState<string[]>([])
    const [renamePitchId, setRenamePitchId] = useState<string>()
    const renameInputRef = useRef<HTMLInputElement>(null)
    const [deleteDialogOpenId, setDeleteDialogueOpenId] = useState<string>()
    const [changePitcherDialogOpenId, setChangePitcherDialogOpenId] = useState<string>()

    useEffect(() => {
        if (renamePitchId && renameInputRef.current) {
          renameInputRef.current.focus()
        }
      },[renamePitchId])

      const handleEditPitcher = (pitcherId: string) => {
        router.push(`/pitchers/${pitcherId}`)
      }
      const handleDeletePitcher = (pitcherId: string) => {
        if (setValues) {
          setValues(prev => ({...prev, PITCHERS: prev.PITCHERS.filter(pitcher => pitcher.id !== pitcherId), PITCHES: prev.PITCHES.filter(pitch => pitch.pitcherId !== pitcherId)}))
        }
      }
      const handleRenamePitch = (pitchId: string) => {
        setRenamePitchId(pitchId)
      }
      const handleChangePitchPitcher = (pitchId: string, prevPitcherId: string, targetPitcherId: string) => {
        const pitch = values.PITCHES.find(pitch => pitch.id === pitchId)
        const prevPitcher = values.PITCHERS.find(pitcher => pitcher.id === prevPitcherId)
        const targetPitcher = values.PITCHERS.find(pitcher => pitcher.id === targetPitcherId)
        if (setValues && pitch && prevPitcher && targetPitcher && prevPitcher?.handedness !== targetPitcher?.handedness) {
          const updatedPitch: Pitch = {...pitch, 
            pitcherId: targetPitcherId, 
            scBallOrientation: new THREE.Vector3(-1 * pitch.scBallOrientation.x, pitch.scBallOrientation.y, pitch.scBallOrientation.z),
            ballOrientation: new THREE.Vector3(-1 * pitch.ballOrientation.x, pitch.ballOrientation.y, pitch.ballOrientation.z),
            relativeSpinTiltDeg: -1 * pitch.relativeSpinTiltDeg,
            gyroDeg: -1 * pitch.gyroDeg,
            armTiltDeg: pitch.armTiltDeg ? -1 * pitch.armTiltDeg : undefined
          }
          setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === pitchId ? updatedPitch : item)}))
        } else if (pitch && setValues) {
          const updatedPitch: Pitch = {...pitch, 
            pitcherId: targetPitcherId
          }
          setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === pitchId ? updatedPitch : item)}))
        }
        setChangePitcherDialogOpenId(undefined)
      }
      const handleClonePitch = (pitchId: string) => {
        const pitch = values.PITCHES.find(pitch => pitch.id === pitchId)
        if (setValues && pitch) {
          const newPitch: Pitch = {...pitch,
            id: uuidv4(),
            name: `(Copy) ${pitch.name}`
          }
          setValues(prev => ({...prev, PITCHES: [...prev.PITCHES, newPitch]}))
        }
      }
      const handleDeletePitch = (pitchId: string) => {
        setValues ? setValues(prev => ({...prev, PITCHES: prev.PITCHES.filter(pitch => pitch.id !== pitchId)})) : {}
      }
      const savePitchName = (value: string | undefined, pitchId: string) => {
        const pitch = values.PITCHES.find(pitch => pitch.id === pitchId)
        if (value && value.length > 0 && setValues && pitch) {
          const updatedPitch = {...pitch, name: value, slug: value.toLowerCase().trim().split(" ").join('-')}
          setValues(prev => ({...prev, PITCHES: prev.PITCHES.map(item => item.id === pitchId ? updatedPitch : item)}))
        }
        setRenamePitchId(undefined)
      }

    return (
        <div className="w-full self-center max-w-lg flex flex-col gap-2 p-4">
          {values.PITCHERS.length === 0 && (
                        <div className="flex flex-col items-center justify-center gap-2 text-center">
                        <p className="text-sm">Oops, looks like you haven't created a pitcher yet. Get started by creating a pitcher, then adding pitches.</p>
                        <Button onClick={() => router.push('/pitchers/create-pitcher')}>
                            <Plus />
                            Create Pitcher
                        </Button>
                        </div>
                    )}
            {values.PITCHERS.map(pitcher => (
                <Collapsible key={pitcher.id} className="w-full border rounded-xl bg-card shadow"
                    onOpenChange={(isOpen) => isOpen ? setOpenPitchers([...openPitchers, pitcher.id]) : setOpenPitchers(openPitchers.filter(id => id !== pitcher.id)) }
                >
                    <CollapsibleTrigger className="w-full flex justify-between items-center p-4 gap-4">
                            <p className="w-full text-start">{pitcher.name}</p>
                            <div>
                                {openPitchers.includes(pitcher.id) && (
                                    <Minus className="size-4" />
                                )}
                                {!openPitchers.includes(pitcher.id) && (
                                    <Plus className="size-4" />
                                )}
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <div className="relative top-0 right-0">
                                  <MoreHorizontal className="size-4" />
                                  <span className="sr-only">More</span>
                                </div>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent
                                className="w-48 rounded"
                                side={"bottom"}
                                align={"end"}
                              >
                                <DropdownMenuItem onClick={() => handleEditPitcher(pitcher.id)}>
                                  <Edit />
                                  <span>Edit pitcher</span>
                                </DropdownMenuItem>
                                <AlertDialog open={deleteDialogOpenId === pitcher.id}>
                                  <AlertDialogTrigger asChild>
                                    <DropdownMenuItem 
                                      variant='destructive' 
                                      className="data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive dark:data-[variant=destructive]:text-destructive dark:data-[variant=destructive]:focus:text-destructive-foreground dark:data-[variant=destructive]:focus:*:[svg]:!text-destructive-foreground"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        setDeleteDialogueOpenId(pitcher.id)
                                      }}
                                    >
                                      <Trash2 />
                                      <span>Delete pitcher</span>
                                    </DropdownMenuItem>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        {`This action cannot be undone. This will permanently delete ${pitcher.name} and all associated pitches.`}
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel onClick={() => setDeleteDialogueOpenId(undefined)}>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => handleDeletePitcher(pitcher.id)}>{`Delete ${pitcher.name}`}</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </DropdownMenuContent>
                            </DropdownMenu>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="w-full flex flex-col items-center pl-12 pr-8 pb-2 text-sm">
                        {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id).length === 0 && (
                            <p>- No Pitches -</p>
                        )}
                        {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'fastball').length > 0 && (
                            <>
                            <Label className="w-full -ml-4 text-foreground/50 text-xs">Fastballs</Label>
                            <div className="w-full border-l">
                                {values.PITCHES.sort((a,b) => a.name.localeCompare(b.name)).map(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'fastball' ? (
                                    <PitchSubMenuItem key={pitch.id}
                                        pitcher={pitcher}
                                        pitch={pitch}
                                        renamePitchId={renamePitchId}
                                        handleChangePitchPitcher={handleChangePitchPitcher} 
                                        handleClonePitch={handleClonePitch}
                                        handleDeletePitch={handleDeletePitch}
                                        handleNavigationClick={(href) => router.push(href)}
                                        handleRenamePitch={handleRenamePitch}
                                        renameInputRef={renameInputRef}
                                        savePitchName={savePitchName}
                                        deleteDialogOpenId={deleteDialogOpenId}
                                        setDeleteDialogueOpenId={setDeleteDialogueOpenId}
                                        changePitcherDialogOpenId={changePitcherDialogOpenId}
                                        setChangePitcherDialogOpenId={setChangePitcherDialogOpenId}
                                    />
                                ) : null )}
                            </div>
                            </>
                        )}
                        {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'breaking-ball').length > 0 && (
                            <>
                            <Label className="w-full -ml-4 text-foreground/50 text-xs">Breaking Balls</Label>
                            <div className="w-full border-l">
                                {values.PITCHES.sort((a,b) => a.name.localeCompare(b.name)).map(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'breaking-ball' ? (
                                    <PitchSubMenuItem key={pitch.id}
                                        pitcher={pitcher}
                                        pitch={pitch}
                                        renamePitchId={renamePitchId}
                                        handleChangePitchPitcher={handleChangePitchPitcher} 
                                        handleClonePitch={handleClonePitch}
                                        handleDeletePitch={handleDeletePitch}
                                        handleNavigationClick={(href) => router.push(href)}
                                        handleRenamePitch={handleRenamePitch}
                                        renameInputRef={renameInputRef}
                                        savePitchName={savePitchName}
                                        deleteDialogOpenId={deleteDialogOpenId}
                                        setDeleteDialogueOpenId={setDeleteDialogueOpenId}
                                        changePitcherDialogOpenId={changePitcherDialogOpenId}
                                        setChangePitcherDialogOpenId={setChangePitcherDialogOpenId}
                                    />
                                ) : null )}
                            </div>
                            </>
                        )}
                        {values.PITCHES.filter(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'offspeed').length > 0 && (
                            <>
                            <Label className="w-full -ml-4 text-foreground/50 text-xs">Offspeed</Label>
                            <div className="w-full border-l">
                                {values.PITCHES.sort((a,b) => a.name.localeCompare(b.name)).map(pitch => pitch.pitcherId === pitcher.id && pitch.pitchCategory === 'offspeed' ? (
                                    <PitchSubMenuItem key={pitch.id}
                                        pitcher={pitcher}
                                        pitch={pitch}
                                        renamePitchId={renamePitchId}
                                        handleChangePitchPitcher={handleChangePitchPitcher} 
                                        handleClonePitch={handleClonePitch}
                                        handleDeletePitch={handleDeletePitch}
                                        handleNavigationClick={(href) => router.push(href)}
                                        handleRenamePitch={handleRenamePitch}
                                        renameInputRef={renameInputRef}
                                        savePitchName={savePitchName}
                                        deleteDialogOpenId={deleteDialogOpenId}
                                        setDeleteDialogueOpenId={setDeleteDialogueOpenId}
                                        changePitcherDialogOpenId={changePitcherDialogOpenId}
                                        setChangePitcherDialogOpenId={setChangePitcherDialogOpenId}
                                    />
                                ) : null )}
                            </div>
                            </>
                        )}
                        <Button onClick={() => router.push(`/pitches/create-pitch?pitcherId=${pitcher.id}`)}>
                          <Plus />
                          Add New Pitch
                        </Button>
                    </CollapsibleContent>
                </Collapsible>
            ))}
        </div>
    )
}

interface PitchSubMenuItemProps {
    pitcher: Pitcher
    pitch: Pitch
    renamePitchId: string | undefined
    handleNavigationClick: (route: string) => void
    handleRenamePitch: (pitchId: string) => void
    handleChangePitchPitcher: (pitchId: string, prevPitcherId: string, targetPitcherId: string) => void
    handleClonePitch: (pitchId: string) => void
    handleDeletePitch: (pitchId: string) => void
    renameInputRef: RefObject<HTMLInputElement | null>
    savePitchName: (value: string | undefined, pitchId: string) => void
    deleteDialogOpenId: string | undefined
    setDeleteDialogueOpenId: Dispatch<SetStateAction<string | undefined>>
    changePitcherDialogOpenId: string | undefined
    setChangePitcherDialogOpenId: Dispatch<SetStateAction<string | undefined>>
  }
  const PitchSubMenuItem = ({
    pitcher,
    pitch,
    renamePitchId,
    handleNavigationClick,
    handleRenamePitch,
    handleChangePitchPitcher,
    handleClonePitch,
    handleDeletePitch,
    renameInputRef,
    savePitchName,
    deleteDialogOpenId,
    setDeleteDialogueOpenId,
    changePitcherDialogOpenId,
    setChangePitcherDialogOpenId
  }:PitchSubMenuItemProps) => {
    const { values } = useLocalStore()
    const [targetPitcherId, setTargetPitcherId] = useState<string>()
  
    return (
      <div key={pitch.id} className="w-full flex">
        {renamePitchId !== pitch.id && (
          <div className="flex w-full items-center justify-start gap-2">
            <Button variant={'ghost'} className="w-full shrink text-start truncate" onClick={() => handleNavigationClick(`/pitches/${pitch.id}`)}>
                <span className="w-full text-start font-normal truncate">{pitch.name}</span>
            </Button>
            <DropdownMenu>
                <DropdownMenuTrigger>
                <div className="relative top-0 right-0 shrink-0">
                    <MoreHorizontal className="size-4" />
                    <span className="sr-only">More</span>
                </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                className="w-48 rounded-lg"
                side={"bottom"}
                align={"end"}
                >
                <DropdownMenuItem onClick={() => handleRenamePitch(pitch.id)}>
                    <Pencil />
                    <span>Rename</span>
                </DropdownMenuItem>
                <AlertDialog open={changePitcherDialogOpenId === pitch.id}>
                    <AlertDialogTrigger asChild>
                    <DropdownMenuItem onClick={(e) => {
                            e.preventDefault()
                            setChangePitcherDialogOpenId(pitch.id)
                        }}
                    >
                        <PersonStanding />
                        <span>Change pitcher</span>
                    </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                        <AlertDialogTitle>{`Assign ${pitch.name} to a new pitcher`}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {`Select the pitcher you would like to assign ${pitch.name} to.`}
                        </AlertDialogDescription>
                        </AlertDialogHeader>
                        <Select onValueChange={setTargetPitcherId}>
                        <SelectTrigger>
                            <SelectValue placeholder='Select a new pitcher' />
                        </SelectTrigger>
                        <SelectContent>
                            {values.PITCHERS.sort((a,b) => a.name.localeCompare(b.name)).map(pitcher => (
                            <SelectItem key={pitcher.id} value={pitcher.id}>{pitcher.name}</SelectItem>
                            ))}
                        </SelectContent>
                        </Select>
                        <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setChangePitcherDialogOpenId(undefined)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction disabled={!targetPitcherId} onClick={() => handleChangePitchPitcher(pitch.id, pitcher.id, targetPitcherId!)}>{`Save`}</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
                <DropdownMenuItem onClick={() => handleClonePitch(pitch.id)}>
                    <Copy />
                    <span>Clone pitch</span>
                </DropdownMenuItem>
                
                    <AlertDialog open={deleteDialogOpenId === pitch.id}>
                    <AlertDialogTrigger asChild>
                        <DropdownMenuItem 
                        variant='destructive' 
                        className="data-[variant=destructive]:text-destructive data-[variant=destructive]:focus:text-destructive data-[variant=destructive]:*:[svg]:!text-destructive dark:data-[variant=destructive]:text-destructive dark:data-[variant=destructive]:focus:text-destructive-foreground dark:data-[variant=destructive]:focus:*:[svg]:!text-destructive-foreground"
                        onClick={(e) => {
                            e.preventDefault()
                            setDeleteDialogueOpenId(pitch.id)
                        }}
                        >
                        <Trash2 />
                        <span>Delete pitch</span>
                        </DropdownMenuItem>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {`This action cannot be undone. This will permanently delete ${pitch.name}.`}
                        </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                        <AlertDialogCancel onClick={() => setDeleteDialogueOpenId(undefined)}>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeletePitch(pitch.id)}>{`Delete ${pitch.name}`}</AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                    </AlertDialog>
                </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
        {renamePitchId === pitch.id && (
          <div className="w-full flex items-center gap-2">
              <Input ref={renameInputRef} type="text" className="h-fit w-full" defaultValue={pitch.name} />
              <Button variant={'outline'} size={'icon'} onClick={() => savePitchName(renameInputRef.current?.value, pitch.id)} >
                <Check />
              </Button>
          </div>
        )}
      </div>
    )
  }